import { useVistassistantMail } from "@/contexts/VistassistantMailContext";
import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { cardClassName, rowDividerClassName } from "./assistantClasses";
import EventDetailsBlock, {
    canAnswerInvitation,
    eventTimeRange,
} from "./EventDetailsBlock";

/**
 * One event on one line: the day, the title, the time and the place. A click unfolds the details
 * (attendees, description, meeting link), accept or decline for an invitation still to come, and
 * the link to the calendar.
 */
const ChatEventRow = ({ event }: { event: AgendaItem }) => {
    const { t, i18n } = useTranslation();
    const { data: user } = useGetUserProfile();
    const detailsId = useId();
    const [isOpen, setIsOpen] = useState(false);
    // The answer to an invitation updates the event in place.
    const [current, setCurrent] = useState(event);
    // In the assistant: the event opens in its side panel, the line stays a line.
    const { onEventClick } = useVistassistantMail();

    const start = new Date(current.start);
    const place =
        current.location?.displayName || current.organizer?.emailAddress?.name;
    const canAnswer = canAnswerInvitation(current, user);

    return (
        <li className={rowDividerClassName}>
            <button
                aria-controls={onEventClick ? undefined : detailsId}
                aria-expanded={onEventClick ? undefined : isOpen}
                className="flex w-full min-w-0 items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-assistant-chip/60"
                onClick={() =>
                    onEventClick
                        ? onEventClick(current)
                        : setIsOpen((open) => !open)
                }
                type="button"
            >
                <span className="flex w-11 flex-none flex-col items-center rounded-lg bg-assistant-lavender py-1 leading-tight">
                    <span className="text-[10px] font-semibold uppercase text-assistant-muted">
                        {start.toLocaleDateString(i18n.language, {
                            weekday: "short",
                        })}
                    </span>
                    <span className="text-sm font-semibold text-primary-darkest">
                        {start.toLocaleDateString(i18n.language, {
                            day: "numeric",
                        })}
                    </span>
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                    <span
                        className={cn(
                            "truncate text-sm font-semibold text-primary-darkest",
                            current.isCancelled && "line-through",
                        )}
                    >
                        {current.subject || t("vistassistant.untitledEvent")}
                    </span>
                    <span className="truncate text-xs text-assistant-muted">
                        {[
                            eventTimeRange(
                                current,
                                i18n.language,
                                t("dashboard.widgets.calendar.event.allDay"),
                            ),
                            place,
                        ]
                            .filter(Boolean)
                            .join(" · ")}
                    </span>
                </span>
                {current.isCancelled && (
                    <span className="flex-none text-xs font-semibold text-error-darkest">
                        {t("vistassistant.cancelledEvent")}
                    </span>
                )}
                {!onEventClick && (
                    <ChevronDown
                        aria-hidden
                        className={cn(
                            "h-4 w-4 flex-none text-assistant-muted transition-transform",
                            isOpen && "rotate-180",
                        )}
                    />
                )}
            </button>
            {isOpen && (
                <div
                    className="flex flex-col gap-2 border-t border-assistant-cardBorder px-4 py-3 text-primary-darkest"
                    id={detailsId}
                >
                    <EventDetailsBlock
                        canAnswer={canAnswer}
                        event={current}
                        onAnswered={setCurrent}
                        scrollDetails
                    />
                </div>
            )}
        </li>
    );
};

/** The events of an answer, one compact line each, unfolded on click. */
const ChatEventList = ({ events }: { events: AgendaItem[] }) => (
    <ul className={cn(cardClassName, "w-full overflow-hidden")}>
        {events.map((event) => (
            <ChatEventRow key={event.id} event={event} />
        ))}
    </ul>
);

export default ChatEventList;
